/**
 * useLandingContent — language + content for the public Landing page.
 *
 * Content source-of-truth order (highest priority first):
 *   1. API:        GET /landing/content/public/?lang={lang}
 *                  served from the CMS — what the public should see.
 *   2. localStorage cache (5 min TTL) to avoid refetching on every nav.
 *   3. Bundled JSON (content.{lang}.json) as the always-available fallback
 *      when the API errors or hasn't been seeded yet.
 *
 * The bundled JSON is also useful for local dev — the API may not be
 * running, and the page should still look right.
 *
 * The language preference is shared with the dashboard via the same
 * localStorage key `ovacs.lang`, so a visitor's choice carries into the
 * dashboard after signup.
 *
 * Preview mode: when the URL contains `?preview=draft`, we'd want to fetch
 * the DRAFT content (requires a token) instead of published.  v1 leaves
 * that as a TODO — the CMS preview button just opens the page normally;
 * the draft-preview channel will be added in v2.
 *
 * Returns: { lang, dir, isRtl, content, toggleLang, setLang, loading }.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import contentEn from './content.en.json'
import contentAr from './content.ar.json'
import apiConfig from '../../../config/apiConfig'

const STORAGE_KEY = 'ovacs.lang'
const SUPPORTED = new Set(['en', 'ar'])
const DEFAULT_LANG = 'en'

// Cached published payload from the API, keyed by language. 5-min TTL.
const CACHE_KEY = 'ovacs.landingContent.v1'
const CACHE_TTL_MS = 5 * 60 * 1000

function readStoredLang() {
  if (typeof window === 'undefined') return DEFAULT_LANG
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED.has(stored)) return stored
  } catch { /* private mode / quota — fall through */ }
  return DEFAULT_LANG
}

function readCache(lang) {
  try {
    const raw = window.localStorage.getItem(`${CACHE_KEY}.${lang}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (!parsed.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null
    return parsed.payload && typeof parsed.payload === 'object' ? parsed.payload : null
  } catch { return null }
}

function writeCache(lang, payload) {
  try {
    window.localStorage.setItem(
      `${CACHE_KEY}.${lang}`,
      JSON.stringify({ ts: Date.now(), payload }),
    )
  } catch { /* ignore quota errors */ }
}

const BUNDLED = { en: contentEn, ar: contentAr }

/**
 * Deep-merge `override` onto `base`, returning a new object.  Used so that
 * the CMS payload (which may be partial during early edits) seamlessly
 * supplements the bundled JSON's complete shape — empty values in the API
 * payload don't blank out the page.
 */
function deepMerge(base, override) {
  if (override == null) return base
  if (Array.isArray(override)) {
    // Arrays from the CMS replace the bundled array wholesale (otherwise
    // shorter CMS arrays leave stale bundled tail items behind).
    return override
  }
  if (typeof override !== 'object') return override
  const out = Array.isArray(base) ? [...base] : { ...(base || {}) }
  for (const key of Object.keys(override)) {
    const v = override[key]
    if (v === '' || v == null) {
      // Empty string / null in the CMS payload falls back to the bundled
      // value — protects the page from accidentally blank fields.
      if (out[key] == null) out[key] = v
      continue
    }
    if (v && typeof v === 'object' && !Array.isArray(v) && base && typeof base[key] === 'object') {
      out[key] = deepMerge(base[key], v)
    } else {
      out[key] = v
    }
  }
  return out
}

async function fetchPublishedContent(lang) {
  const url = `${apiConfig.API_BASE_URL}/landing/content/public/?lang=${lang}`
  const res = await fetch(url, { method: 'GET' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const body = await res.json()
  return body?.data?.payload || null
}

export function useLandingContent() {
  const [lang, setLangState] = useState(readStoredLang)
  const [remoteEn, setRemoteEn] = useState(() => readCache('en'))
  const [remoteAr, setRemoteAr] = useState(() => readCache('ar'))
  const [loading, setLoading] = useState(true)

  const setLang = useCallback((next) => {
    if (!SUPPORTED.has(next)) return
    setLangState(next)
    try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }, [])

  const toggleLang = useCallback(() => {
    setLang(lang === 'en' ? 'ar' : 'en')
  }, [lang, setLang])

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const isRtl = lang === 'ar'

  // Drive <html lang> + <html dir>.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('lang', lang)
    document.documentElement.setAttribute('dir', dir)
  }, [lang, dir])

  // Sync across tabs.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && SUPPORTED.has(e.newValue)) {
        setLangState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Fetch both languages from the API once on mount.  Failures fall back to
  // the bundled JSON silently — the page never goes blank.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [en, ar] = await Promise.allSettled([
          fetchPublishedContent('en'),
          fetchPublishedContent('ar'),
        ])
        if (cancelled) return
        if (en.status === 'fulfilled' && en.value) {
          setRemoteEn(en.value); writeCache('en', en.value)
        }
        if (ar.status === 'fulfilled' && ar.value) {
          setRemoteAr(ar.value); writeCache('ar', ar.value)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const content = useMemo(() => {
    const bundled = BUNDLED[lang] || BUNDLED.en
    const remote = lang === 'ar' ? remoteAr : remoteEn
    if (!remote || Object.keys(remote).length === 0) return bundled
    return deepMerge(bundled, remote)
  }, [lang, remoteEn, remoteAr])

  return { lang, dir, isRtl, content, toggleLang, setLang, loading }
}
